import {
  Box,
  Text,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Flex,
  Spacer,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import { EligibilityAggregateInsightsGqlDataGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  ExpandedEligibilityAggregateInsightsData,
  PortfolioChartConfig,
  portfolioFormatterMap,
} from '../shared/portfolioChartUtils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { ToggleIcon } from 'packages/common-frontend/components/ToggleIcon';

const headers = [
  'Show',
  '',
  'Eligibility Test',
  'Kickout Amount',
  'Percent of Ineligible',
];

// we have a special toggle for show/hid total net eligibility kickout
const totalEligibilityFieldName: keyof EligibilityAggregateInsightsGqlDataGQLType =
  'totalNetEligibilityKickout';

export const PortfolioEligibilityKickoutsChartDetailsTable: React.FC<{
  selectedBarData: ExpandedEligibilityAggregateInsightsData;
  chartConfig: PortfolioChartConfig<any>;
  chartTitle: string;
  activeFields: (keyof ExpandedEligibilityAggregateInsightsData)[];
  onToggleActiveField: (
    selectedField: keyof ExpandedEligibilityAggregateInsightsData,
  ) => void;
}> = ({
  selectedBarData,
  chartConfig,
  chartTitle,
  activeFields,
  onToggleActiveField,
}) => {
  return (
    <Box width="100%">
      <Flex>
        <Text fontSize="16px" fontWeight={600} mb="24px">
          Reporting Date:{' '}
          {commonDate.fullDateStrToHumanDateString(
            selectedBarData.reportingDate,
          )}
        </Text>
        <Spacer />
        <Flex
          cursor="pointer"
          onClick={() => onToggleActiveField(totalEligibilityFieldName)}
        >
          <ToggleIcon
            isActive={_.includes(activeFields, totalEligibilityFieldName)}
          />
          <Text ml="16px" fontWeight={500} fontSize="16px">
            Total Eligiblity Kickout
          </Text>
        </Flex>
      </Flex>

      <TableContainer>
        <Table borderWidth="1px">
          <Thead borderBottomWidth="2px">
            <Tr>
              {_.map(headers, (header) => (
                <Th
                  key={`portfolio-${chartTitle}-table-header-${header}`}
                  textTransform="none"
                  fontSize="16px"
                  fontWeight={500}
                  color={colors.black[95]}
                  letterSpacing="normal"
                  textAlign={header === 'Show' ? 'center' : undefined}
                >
                  {header}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {_.map(chartConfig, (config, fieldName) => {
              if (!config || fieldName === totalEligibilityFieldName) {
                return (
                  <React.Fragment
                    key={`portfolio-${chartTitle}-table-row-${fieldName}`}
                  />
                );
              }
              const { label, format, color, activeColor } = config;
              const formattedValue = portfolioFormatterMap[format](
                selectedBarData[fieldName],
              );
              const isActive = _.includes(activeFields, fieldName);

              const percentOfIneligible = commonUtils.divideByOrZero(
                _.toNumber(selectedBarData[fieldName]),
                selectedBarData.totalGrossEligibilityKickout,
              );

              return (
                <Tr key={`portfolio-${chartTitle}-table-row-${fieldName}`}>
                  <Td
                    cursor="pointer"
                    onClick={() => onToggleActiveField(fieldName)}
                    textAlign="center"
                  >
                    <ToggleIcon isActive={isActive} />
                  </Td>
                  <Td textAlign="center">
                    <Icon
                      icon="square"
                      type="solid"
                      color={activeColor ?? color}
                    />
                  </Td>
                  <Td
                    fontSize="16px"
                    color={colors.black[70]}
                    wordBreak="break-word"
                    whiteSpace="normal"
                  >
                    {label}
                  </Td>
                  <Td fontSize="16px" color={colors.black[70]}>
                    {formattedValue}
                  </Td>
                  <Td fontSize="16px" color={colors.black[95]}>
                    {portfolioFormatterMap.percentage(percentOfIneligible)}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
